import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  AuthContainer,
  Logo,
  AuthBox,
  FormContainer,
  LinkContainer,
} from '../../styles/Auth.styles';

const FirstTimePasswordChange = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { changePasswordFirstLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const userId = location.state?.userId;
  const email = location.state?.email;
  
  // Redirect to login if no userId is provided
  if (!userId) {
    navigate('/login');
    return null;
  }
  
  // Mask email for display
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const maskedUsername = username.charAt(0) + 
      '*'.repeat(Math.max(1, username.length - 2)) + 
      (username.length > 1 ? username.charAt(username.length - 1) : '');
    return `${maskedUsername}@${domain}`;
  };

  const validatePassword = (password) => {
    if (password.length < 12) {
      return { valid: false, error: 'Password must be at least 12 characters long' };
    }
    
    const digitCount = (password.match(/\d/g) || []).length;
    if (digitCount < 1) {
      return { valid: false, error: 'Password must contain at least 1 digit' };
    }
    
    const specialCharCount = (password.match(/[!@#$%^&*(),.?":{}|<>-]/g) || []).length;
    if (specialCharCount < 1) {
      return { valid: false, error: 'Password must contain at least 1 special character' };
    }
    
    return { valid: true };
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.error);
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const user = await changePasswordFirstLogin(userId, newPassword);
      if (user.role === 'admin' || user.role === 'accountant') {
        navigate('/reporting');
      } else {
        navigate('/timecard');
      }
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthContainer>
      <Logo
        src="/images/eci_logo_color.svg"
        alt="ECI Logo"
      />
      <AuthBox>
        <Typography component="h1" variant="h5" align="center" gutterBottom>
          Change Password
        </Typography>
        <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
          This is your first time logging in. Please change your password to continue.
          {email && (
            <Box component="span" display="block" mt={1}>
              Account: {maskEmail(email)}
            </Box>
          )}
        </Typography>
        
        <FormContainer component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Password must be at least 12 characters long with at least 1 digit and 1 special character.
          </Typography>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LinkContainer>
              <Link to="/login">
                Back to Login
              </Link>
            </LinkContainer>
          </Box>
        </FormContainer>
      </AuthBox>
    </AuthContainer>
  );
};

export default FirstTimePasswordChange;
